import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function SluzbyDetailne() {
  const location = useLocation();

  useEffect(() => {
    // Update page title and meta description
    document.title = 'Detailní AI služby | Kompletní specifikace AI řešení | CzechAI';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Detailní přehled všech AI služeb CzechAI. 10 hlavních kategorií s kompletními specifikacemi jednotlivých služeb pro AI transformaci vaší firmy.');
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Detailní AI služby",
      "description": "Kompletní specifikace AI řešení pro firmy",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/sluzby-detailne`,
      "mainEntity": {
        "@type": "ItemList",
        "name": "AI služby",
        "numberOfItems": 10,
        "itemListElement": [
          {
            "@type": "Service",
            "position": 1,
            "name": "AI pro obchod a zákazníky",
            "description": "Inteligentní řešení pro zlepšení vztahů se zákazníky"
          },
          {
            "@type": "Service", 
            "position": 2,
            "name": "Automatizace procesů",
            "description": "Eliminace rutinních úkolů pomocí AI"
          },
          {
            "@type": "Service",
            "position": 3, 
            "name": "Chatboti & zákaznický servis",
            "description": "Inteligentní chatboti pro 24/7 podporu"
          }
        ]
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Domů",
            "item": import.meta.env.VITE_SITE_URL || "https://example.com"
          },
          {
            "@type": "ListItem", 
            "position": 2,
            "name": "Služby",
            "item": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/sluzby`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Detailní služby"
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      id: 'obchod-zakaznici',
      title: '1. AI pro obchod a zákazníky',
      icon: 'ri-customer-service-2-line',
      shortDesc: 'Inteligentní řešení pro zlepšení vztahů se zákazníky a optimalizaci obchodních procesů',
      subcategories: [
        {
          id: 'ai-obchodni-zastupce',
          name: 'AI Obchodní zástupce',
          description: 'Virtuální obchodní zástupce s přístupem k vašemu CRM, který dokáže vést prodejní rozhovory, qualifikovat leady a uzavírat obchody. Pracuje 24/7 a netrpí únavou ani špatnou náladou. Integruje se s existujícími CRM systémy a může být nakonfigurován podle vašich prodejních procesů.'
        },
        {
          id: 'ai-customer-support',
          name: 'AI Customer Support',
          description: 'Inteligentní zákaznický servis, který dokáže vyřešit 85% dotazů okamžitě. Integruje se s vašimi systémy a při složitých problémech plynule předává případy lidským agentům. Podporuje více kanálů komunikace a udržuje konzistentní úroveň služeb.'
        },
        {
          id: 'ai-recepcni',
          name: 'AI Recepční / Asistentka',
          description: 'Virtuální recepční, která přijímá hovory, domlouvá schůzky, spravuje kalendář a poskytuje základní informace o vaší firmě. Nikdy není nemocná a vždy je zdvořilá. Dokáže obsloužit více hovorů současně a integruje se s kalendářními systémy.'
        },
        {
          id: 'ai-personalista',
          name: 'AI Personalista (HR)',
          description: 'AI asistent pro HR procesy - screening životopisů, plánování pohovorů, onboarding nových zaměstnanců a odpovídání na časté HR dotazy. Šetří desítky hodin týdně a zajišťuje konzistentní HR procesy napříč organizací.'
        },
        {
          id: 'ai-social-media-manager',
          name: 'AI Social Media Manager',
          description: 'Automatické vytváření a publikování obsahu na sociálních sítích, odpovídání na komentáře, monitoring zmínek o značce a analýza výkonnosti kampaní. Udržuje aktivní prezenci na všech důležitých platformách.'
        },
        {
          id: 'ai-copywriter-seo',
          name: 'AI Copywriter & SEO',
          description: 'Tvorba SEO článků, produktových popisů, e-mailových kampaní a dalšího marketingového obsahu. Optimalizováno pro vyhledávače a konverze. Udržuje konzistentní brand voice a automaticky optimalizuje obsah pro lepší výkon.'
        },
        {
          id: 'ai-pravni-asistent',
          name: 'AI Právní asistent',
          description: 'Pomoc s přípravou smluv, kontrola právních dokumentů, monitoring legislativních změn a poskytování základních právních informací. Snižuje náklady na právní služby a zrychluje zpracování dokumentace.'
        },
        {
          id: 'ai-financni-analytik',
          name: 'AI Finanční analytik',
          description: 'Analýza finančních dat, predikce cash flow, monitoring nákladů, automatické reportování a identifikace finančních rizik a příležitostí. Poskytuje cenné insights pro strategické rozhodování.'
        },
        {
          id: 'ai-sales-coach',
          name: 'AI Sales Coach',
          description: 'Trénink prodejního týmu pomocí simulací, analýza prodejních rozhovorů, poskytování personalizovaných tipů a tracking výkonnosti jednotlivých prodejců. Kontinuálně zlepšuje prodejní dovednosti týmu.'
        },
        {
          id: 'ai-voice-to-crm',
          name: 'AI Voice-to-CRM',
          description: 'Automatické převádění hlasových nahrávek z obchodních schůzek a hovorů přímo do CRM systému, včetně identifikace action items a follow-up úkolů. Eliminuje manuální zadávání dat po schůzkách.'
        }
      ]
    },
    {
      id: 'automatizace-procesu',
      title: '2. Automatizace procesů',
      icon: 'ri-robot-2-line',
      shortDesc: 'Eliminujte rutinní úkoly a zefektivněte své podnikové procesy pomocí AI',
      subcategories: [
        {
          id: 'automaticke-zpracovani-faktur',
          name: 'Automatické zpracování faktur',
          description: 'AI čte, kategorizuje a zpracovává příchozí faktury, automaticky je páruje s objednávkami a připravuje k schválení. Eliminuje manuální data entry a snižuje chyby při zpracování účetních dokumentů.'
        },
        {
          id: 'automatizace-skladu-logistiky',
          name: 'Automatizace skladu & logistiky',
          description: 'Optimalizace skladových procesů, automatické objednávání zásob na základě predikce poptávky, routing dodávek a tracking zásilek. Minimalizuje náklady na skladování a zlepšuje dostupnost produktů.'
        },
        {
          id: 'automaticke-reporty-kpi',
          name: 'Automatické reporty (KPI)',
          description: 'Pravidelné generování a distribuce reportů o klíčových metrikách, automatické upozornění při výjimkách a trendy v datech. Poskytuje aktuální přehled o výkonnosti firmy bez manuální práce.'
        },
        {
          id: 'ai-sprava-dokumentu',
          name: 'AI správa dokumentů',
          description: 'Automatická klasifikace, archivace a vyhledávání v dokumentech. OCR pro naskenované dokumenty a inteligentní tagging. Rychlé nalezení jakéhokoliv dokumentu a automatické udržování pořádku.'
        },
        {
          id: 'automatizace-onboardingu',
          name: 'Automatizace onboardingu',
          description: 'Automatický proces zaškolování nových zaměstnanců - od vytvoření účtů až po dodání vybavení a vstupní školení. Zajišťuje konzistentní a efektivní začlenění nových členů týmu.'
        },
        {
          id: 'ai-meeting-assistant',
          name: 'AI Meeting Assistant',
          description: 'Nahrávání a transkripce schůzek, automatické vytváření zápisů a action items, rozesílání follow-up úkolů účastníkům. Nikdo už nezapomene na důležité body ze schůzek.'
        },
        {
          id: 'ai-contract-management',
          name: 'AI Contract Management',
          description: 'Správa smluv - upozornění na vypršení, sledování milníků, automatické prodlužování a analýza podmínek smluv. Minimalizuje rizika spojená s neaktuální dokumentací.'
        },
        {
          id: 'ai-expense-control',
          name: 'AI Expense Control',
          description: 'Automatické zpracování cestovních náhrad a výdajů, kontrola compliance s firemními pravidly a detekce podezřelých transakcí. Zjednodušuje administraci a zvyšuje transparentnost nákladů.'
        }
      ]
    },
    {
      id: 'chatbott-servis',
      title: '3. Chatboti & zákaznický servis',
      icon: 'ri-chat-bot-line',
      shortDesc: 'Inteligentní chatboti pro dokonalý zákaznický zážitek 24/7',
      subcategories: [
        {
          id: 'hyper-personalizovany-nakupni-asistent',
          name: 'Hyper-personalizovaný nákupní asistent',
          description: 'AI asistent, který zná preference zákazníka a dokáže doporučit produkty na míru. Integruje se s e-shopem a CRM pro úplnou personalizaci. Zvyšuje konverze a spokojenost zákazníků.'
        },
        {
          id: 'ai-hlasovy-asistent',
          name: 'AI Hlasový asistent',
          description: 'Hlasový bot pro telefonní linky, který dokáže vyřídit základní dotazy, přesměrovat hovory a zaznamenat zprávy. Funguje i mimo pracovní hodiny a poskytuje konzistentní služby.'
        },
        {
          id: 'lead-gen-chatbot',
          name: 'Lead-gen chatbot',
          description: 'Specializovaný chatbot pro získávání kontaktů návštěvníků webu. Kvalifikuje leady, sbírá kontaktní údaje a předává hot leady prodejnímu týmu. Automatizuje první fázi prodejního procesu.'
        },
        {
          id: 'analyticky-chatbot',
          name: 'Analytický chatbot',
          description: 'Interní bot pro zaměstnance, který umí odpovídat na dotazy o firemních datech, generovat reporty a poskytovat business insights. Demokratizuje přístup k datům ve firmě.'
        },
        {
          id: 'proaktivni-servisni-chatbot',
          name: 'Proaktivní servisní chatbot',
          description: 'Bot, který proaktivně kontaktuje zákazníky - připomíná termíny, nabízí doplňkové služby a informuje o novinkách. Zvyšuje customer lifetime value a udržuje vztahy se zákazníky.'
        },
        {
          id: 'multijazyčny-ai-chatbot',
          name: 'Multijazyčný AI chatbot',
          description: 'Chatbot podporující více jazyků pro mezinárodní firmy. Automatická detekce jazyka a kulturně přizpůsobené odpovědi pro různé trhy. Rozšiřuje dosah na globální úrovni.'
        }
      ]
    },
    {
      id: 'marketing-prodej',
      title: '4. Marketing & prodej',
      icon: 'ri-rocket-2-line',
      shortDesc: 'AI nástroje pro personalizovaný marketing a optimalizaci prodeje',
      subcategories: [
        {
          id: 'prediktivni-analyza-zakazniku',
          name: 'Prediktivní analýza zákazníků',
          description: 'AI predikuje chování zákazníků, pravděpodobnost nákupu, churn risk a lifetime value. Pomáhá lépe cílit marketingové kampaně a optimalizovat investice do akvizice zákazníků.'
        },
        {
          id: 'dynamicka-cenotvorba',
          name: 'Dynamická cenotvorba',
          description: 'Automatické přizpůsobování cen na základě poptávky, konkurence, sezónnosti a dalších faktorů. Maximalizuje zisky při zachování konkurenceschopnosti. Real-time optimalizace podle tržních podmínek.'
        },
        {
          id: 'personalizace-webu-emailu',
          name: 'Personalizace webu & e-mailů',
          description: 'Každý návštěvník vidí obsah přizpůsobený jeho preferencím a chování. Zvyšuje konverze a engagement. Dynamicky upravuje obsah, nabídky a doporučení na základě uživatelského profilu.'
        },
        {
          id: 'ai-pro-influencer-marketing',
          name: 'AI pro influencer marketing',
          description: 'Identifikace vhodných influencerů, analýza jejich audience, monitoring kampaní a měření ROI influencer marketingu. Automatizuje výběr partnerů a optimalizuje investice do spolupráce.'
        },
        {
          id: 'generovani-vizualu-videi',
          name: 'Generování vizuálů & videí',
          description: 'AI tvorba obrázků, grafik a videí pro marketingové kampaně. Rychlé vytváření A/B variant kreativ pro testování. Snižuje náklady na kreativní produkci a zrychluje time-to-market.'
        },
        {
          id: 'ai-ad-optimizer',
          name: 'AI Ad Optimizer',
          description: 'Automatická optimalizace PPC kampaní - bidding, targeting, ad copy testing. Zvyšuje ROAS a snižuje CPC. Kontinuálně testuje a optimalizuje všechny aspekty placené reklamy.'
        },
        {
          id: 'generator-landing-pages',
          name: 'Generátor landing pages',
          description: 'AI vytváří a testuje různé varianty landing pages na základě cílové skupiny a kampaně. Automatické A/B testování pro maximální konverze. Optimalizuje design i obsah podle výkonnosti.'
        }
      ]
    },
    {
      id: 'skoleni-rozvoj',
      title: '5. Školení & rozvoj',
      icon: 'ri-graduation-cap-line',
      shortDesc: 'Personalizované vzdělávání a rozvoj zaměstnanců pomocí AI',
      subcategories: [
        {
          id: 'personalizovane-vzdelavani',
          name: 'Personalizované vzdělávání',
          description: 'AI vytváří individuální učební plány pro každého zaměstnance na základě jeho role, dovedností a cílů. Adaptivní obsah se přizpůsobuje tempu učení a zajišťuje optimální progres každého jednotlivce.'
        },
        {
          id: 'ai-simulator-rozhovoru',
          name: 'AI Simulátor rozhovorů',
          description: 'Virtuální trénink prodejních rozhovorů, job interviews nebo customer service situací. AI poskytuje okamžitou zpětnou vazbu a tipy pro zlepšení. Bezpečné prostředí pro cvičení složitých situací.'
        },
        {
          id: 'ai-jazykovy-kouc',
          name: 'AI Jazykový kouč',
          description: 'Personalizovaná výuka jazyků s AI koučem, který se přizpůsobuje úrovni a stylu učení. Konverzační praxe s AI native speakerem. Flexibilní učení podle individuálního rozvrhu.'
        },
        {
          id: 'ai-skoleni-compliance-bozp',
          name: 'AI školení compliance/BOZP',
          description: 'Interaktivní compliance a bezpečnostní školení s AI scenáři. Automatické testování znalostí a certifikace. Evidence školení pro audit. Pravidelné aktualizace podle změn v legislativě.'
        },
        {
          id: 'ai-leadership-mentor',
          name: 'AI Leadership Mentor',
          description: 'AI mentor pro rozvoj leadership dovedností. Personalizované rady, case studies a situační tréninky pro management pozice. Kontinuální rozvoj vedoucích dovedností s okamžitou zpětnou vazbou.'
        },
        {
          id: 'microlearning-platforma',
          name: 'Microlearning platforma',
          description: 'Krátké, zaměřené lekce doručované AI na základě pracovního kalendáře a aktuálních potřeb zaměstnance. Learning v malých dávkách optimalizuje retenční křivku a minimalizuje časovou zátěž.'
        }
      ]
    },
    {
      id: 'analytika-bi',
      title: '6. Analytika & BI',
      icon: 'ri-bar-chart-line',
      shortDesc: 'Transformujte vaše data na užitečné poznatky a predikce',
      subcategories: [
        {
          id: 'analyza-sentimentu',
          name: 'Analýza sentimentu',
          description: 'AI analyzuje sentiment zákazníků z recenzí, social media a zákaznických interakcí. Včasné detekce problémů a příležitostí pro zlepšení. Monitoring reputace v real-time napříč všemi kanály.'
        },
        {
          id: 'predikce-fluktuace-zamestnancu',
          name: 'Predikce fluktuace zaměstnanců',
          description: 'AI predikuje, kteří zaměstnanci mají vysoké riziko odchodu. Umožňuje proaktivní retention strategie a snížení nákladů na nábor. Identifikuje faktory ovlivňující spokojenost zaměstnanců.'
        },
        {
          id: 'konkurenci-analyza',
          name: 'Konkurenční analýza',
          description: 'Automatické sledování konkurence - ceny, produkty, marketing strategie. AI identifikuje trendy a příležitosti na trhu. Kontinuální monitoring konkurenčního prostředí a včasné varování před změnami.'
        },
        {
          id: 'optimalizace-vyroby',
          name: 'Optimalizace výroby',
          description: 'AI optimalizuje výrobní procesy, predikuje poruchy strojů a minimalizuje waste. Zvyšuje efektivitu a snižuje náklady. Prediktivní údržba prodlužuje životnost zařízení.'
        },
        {
          id: 'ai-cashflow-predictor',
          name: 'AI Cashflow Predictor',
          description: 'Predikce cash flow na základě historických dat, sezónnosti a tržních trendů. Pomáhá s finančním plánováním a řízením likvidity. Včasné varování před finančními problémy.'
        },
        {
          id: 'sustainability-analytics',
          name: 'Sustainability Analytics',
          description: 'Monitoring a analýza udržitelnosti - carbon footprint, waste management, energy efficiency. Reporting pro ESG compliance. Identifikace příležitostí pro zlepšení environmentálního dopadu.'
        }
      ]
    },
    {
      id: 'odborne-sluzby',
      title: '7. Odborné a důvěrné služby',
      icon: 'ri-shield-check-line',
      shortDesc: 'Specializované AI řešení pro strategické a rizikové analýzy',
      subcategories: [
        {
          id: 'ai-due-diligence',
          name: 'AI Due Diligence',
          description: 'Komplexní due diligence analýza firem pomocí AI - finanční health check, reputační analýza, právní rizika. Rychlejší a důkladnější než tradiční metody. Automatické prověření všech dostupných zdrojů informací.'
        },
        {
          id: 'generativni-ai-strategie',
          name: 'Generativní AI strategie',
          description: 'Strategické poradenství pro implementaci AI v organizaci. Roadmapa, risk assessment, ROI kalkulace a change management pro AI transformaci. Individuální přístup podle specifik firmy.'
        },
        {
          id: 'ai-risk-scoring',
          name: 'AI Risk Scoring',
          description: 'AI model pro hodnocení rizik - kreditní, operační, compliance. Automatické skórování a monitoring rizikových faktorů v real-time. Predikce potenciálních problémů před jejich vznikem.'
        },
        {
          id: 'scenario-planning-ai',
          name: 'Scenario Planning AI',
          description: 'AI-powered scénářové plánování pro strategické rozhodování. Simulace různých business scenáriů a jejich dopadů na firmu. Příprava na různé budoucí situace s kvantifikovanými dopady.'
        }
      ]
    },
    {
      id: 'research-analyzy',
      title: '8. Research & analýzy',
      icon: 'ri-search-2-line',
      shortDesc: 'Komplexní výzkum a analýzy trhu, konkurence a příležitostí',
      subcategories: [
        {
          id: 'due-diligence-firem',
          name: 'Due diligence firem',
          description: 'Kompletní background check firem včetně finanční stability, reputace, legal issues a business connections. Automatizovaný sběr a analýza dat z veřejných i proprietárních zdrojů.'
        },
        {
          id: 'customer-persona-research',
          name: 'Customer persona research',
          description: 'AI analyzuje data o zákaznících a vytváří detailní buyer persony včetně behavioral patterns, preferences a journey mapping. Hluboké porozumění cílové skupině pro lepší marketing.'
        },
        {
          id: 'tender-monitoring',
          name: 'Tender monitoring',
          description: 'Automatické sledování veřejných zakázek a tendrů relevantních pro vaše business. Alerting a kompetitivní analýza tendrů. Identifikace nových obchodních příležitostí.'
        },
        {
          id: 'investment-screening',
          name: 'Investment screening',
          description: 'AI screening investičních příležitostí na základě definovaných kritérií. Due diligence startupů a investičních projektů. Automatické hodnocení potenciálu a rizik investic.'
        },
        {
          id: 'compliance-monitoring',
          name: 'Compliance monitoring',
          description: 'Kontinuální monitoring regulatorních změn a compliance requirements. Automatické alerting při changes affecting your business. Predikce dopadů nové legislativy na vaše podnikání.'
        },
        {
          id: 'brand-sentiment-analysis',
          name: 'Brand sentiment analysis',
          description: 'Monitorování zmínek o značce napříč internetem a social media. Sentiment analýza a reputation management. Včasná detekce PR krizí a měření efektivity brandingových kampaní.'
        },
        {
          id: 'konkurenci-monitoring',
          name: 'Konkurenční monitoring',
          description: 'Sledování konkurence - produkty, ceny, marketing strategie, job postings. Competitive intelligence dashboard. Analýza konkurenčních tahů a identifikace gaps na trhu.'
        },
        {
          id: 'lead-generation',
          name: 'Lead generation',
          description: 'AI-powered lead generation na základě ideálního customer profilu. Automatické qualification a contact enrichment. Kontinuální dodávka kvalitních leadů do sales pipeline.'
        },
        {
          id: 'market-research',
          name: 'Market research',
          description: 'Automated market research včetně size analysis, trend identification, customer needs assessment a competitive landscape. Rychlé získání market insights pro strategické rozhodování.'
        },
        {
          id: 'supplier-scouting',
          name: 'Supplier scouting',
          description: 'AI hledání a hodnocení potenciálních dodavatelů na základě vašich požadavků. Risk assessment a performance prediction. Optimalizace supply chain s novými partnery.'
        },
        {
          id: 'talent-market-analysis',
          name: 'Talent Market Analysis',
          description: 'Analýza trhu práce pro specific roles - salary benchmarks, skills requirements, talent availability a recruiting strategies. Optimalizace HR strategie podle aktuálního trhu.'
        },
        {
          id: 'technology-radar',
          name: 'Technology Radar',
          description: 'Monitoring emerging technologies relevantních pro vaše industry. Technology trend analysis a innovation opportunities identification. Včasná identifikace disruptivních technologií.'
        }
      ]
    },
    {
      id: 'content-marketing',
      title: '9. Content & marketing',
      icon: 'ri-article-line',
      shortDesc: 'AI generování obsahu pro všechny marketingové potřeby',
      subcategories: [
        {
          id: 'seo-clanky-blogy',
          name: 'SEO články a blogy',
          description: 'AI tvorba SEO optimalizovaných článků a blog postů. Keyword research, content planning a on-page optimalizace pro lepší ranking. Konzistentní publikování kvalitního obsahu zvyšuje organický reach.'
        },
        {
          id: 'planovani-socialnich-siti',
          name: 'Plánování sociálních sítí',
          description: 'AI content kalendář pro social media s automatickým generováním postů, optimal timing a cross-platform adaptace obsahu. Udržuje aktivní prezenci na všech důležitých kanálech.'
        },
        {
          id: 'email-marketing-kampane',
          name: 'E-mail marketing kampaně',
          description: 'Personalizované e-mail kampaně s AI-generated obsahem, subject line optimization a behavioral triggering. Automatizace celého email marketingového funelu s vysokými otevíracími a click-through rates.'
        },
        {
          id: 'whitepapers-case-studies',
          name: 'Whitepapers a case studies',
          description: 'AI asistované vytváření complex content jako whitepapers, case studies a thought leadership články pro B2B marketing. Budování expertní pozice a generování kvalitních leadů.'
        },
        {
          id: 'prezentace-pitch-decky',
          name: 'Prezentace a pitch decky',
          description: 'AI pomáhá vytvářet persuasive prezentace a pitch decky s optimální story flow a visual design recommendations. Efektivní komunikace hodnoty produktu nebo služby.'
        },
        {
          id: 'produktove-popisy',
          name: 'Produktové popisy',
          description: 'Automatické generování produktových popisů pro e-commerce s SEO optimalizací a conversion-focused copywriting. Škálovatelná tvorba obsahu pro velké produktové katalogy.'
        },
        {
          id: 'tiskove-zpravy',
          name: 'Tiskové zprávy',
          description: 'AI-powered newsletter creation s personalizovaným obsahem pro různé subscriber segmenty a automated scheduling. Udržování vztahů se zákazníky prostřednictvím hodnotného obsahu.'
        },
        {
          id: 'linkedin-branding',
          name: 'LinkedIn branding',
          description: 'Personal branding na LinkedIn - AI generované posty, články a networking messages pro thought leadership building. Budování profesionální reputation a rozšiřování business sítě.'
        },
        {
          id: 'videoscenare',
          name: 'Videoscénáře',
          description: 'Tvorba scénářů pro marketing videa, explainer videos a social media content s engagement optimization. Efektivní video obsah pro různé platformy a cílové skupiny.'
        },
        {
          id: 'newslettery',
          name: 'Newslettery',
          description: 'AI-powered newsletter creation s personalizovaným obsahem pro různé subscriber segmenty a automated scheduling. Udržování vztahů se zákazníky prostřednictvím hodnotného obsahu.'
        },
        {
          id: 'ai-design-assistant',
          name: 'AI Design Assistant',
          description: 'AI asistované vytváření visual content - grafiky, infografiky a simple design elements pro marketing campaigns. Rychlá tvorba vizuálů bez potřeby designéra.'
        },
        {
          id: 'podcast-audio-generator',
          name: 'Podcast & Audio generátor',
          description: 'AI tools pro podcast production - script writing, voice synthesis a audio content optimization pro různé platformy. Škálovatelná tvorba audio obsahu pro content marketing.'
        }
      ]
    },
    {
      id: 'automatizace-monitoring',
      title: '10. Automatizace & monitoring',
      icon: 'ri-dashboard-line',
      shortDesc: 'Komplexní automatizace a monitoring všech klíčových procesů ve firmě',
      subcategories: [
        {
          id: 'automatizace-opakovanych-ukolu',
          name: 'Automatizace opakujících se úkolů',
          description: 'RPA boti pro automatizaci routine tasks - data entry, file transfers, system updates. Eliminace manual work a human errors. Škálovatelná automatizace napříč všemi odděleními firmy.'
        },
        {
          id: 'chatboty-zakaznicka-podpora',
          name: 'Chatboty pro zákaznickou podporu',
          description: 'Advanced customer service chatbots s NLP a integration s CRM. Multi-channel support a seamless handoff to human agents. Kontinuální zlepšování na základě customer feedback.'
        },
        {
          id: 'personalizace-marketingovych-kampani',
          name: 'Personalizace marketingových kampaní',
          description: 'AI-driven campaign personalization na základě customer behavior, preferences a real-time data. Dynamic content optimization pro maximální relevanci a engagement každého zákazníka.'
        },
        {
          id: 'skoleni-zamestnancu-ai-moduly',
          name: 'Školení zaměstnanců pomocí AI modulů',
          description: 'Intelligent tutoring systems s adaptive learning paths, skill assessment a personalized training recommendations. Efektivní rozvoj lidských zdrojů s měřitelnými výsledky.'
        },
        {
          id: 'monitoring-procesu-kpi',
          name: 'Monitoring procesů a KPI',
          description: 'Real-time monitoring business procesů s AI anomaly detection, performance alerts a automated reporting dashboards. Proaktivní řízení na základě aktuálních dat a trendů.'
        },
        {
          id: 'prediktivni-monitoring',
          name: 'Prediktivní monitoring',
          description: 'Predictive analytics pro early warning systems - equipment failures, supply chain disruptions a market changes. Předcházení problémům před jejich vznikem s konkrétními doporučeními.'
        },
        {
          id: 'real-estate-portfolio-analyza',
          name: 'Real estate portfolio analýza',
          description: 'AI analýza nemovitostního portfolia - valuation trends, investment opportunities, risk assessment a portfolio optimization. Komplexní správa nemovitostních investic s predikčními modely.'
        },
        {
          id: 'optimalizace-zasob',
          name: 'Optimalizace zásob',
          description: 'Inventory optimization s demand forecasting, automated reordering a supply chain optimization pro minimalizaci costs. Ideální úroveň zásob bez rizika výpadků nebo překapitalizování.'
        },
        {
          id: 'sales-forecasting',
          name: 'Sales forecasting',
          description: 'Accurate sales predictions pomocí AI analysis historical data, market trends a pipeline intelligence pro better planning. Spolehlivé plánování výnosů a zdrojů na základě dat.'
        },
        {
          id: 'facility-management-ai',
          name: 'Facility Management AI',
          description: 'Smart building management s IoT integration, energy optimization, predictive maintenance a space utilization analytics. Efektivní správa komerčních nemovitostí s minimálními náklady.'
        },
        {
          id: 'ai-security-monitoring',
          name: 'AI Security Monitoring',
          description: 'Intelligent security monitoring s threat detection, behavioral analysis a automated incident response pro cyber security. Proaktivní ochrana před kybernetickými hrozbami s rychlou reakcí.'
        }
      ]
    }
  ];

  // Handle URL hash navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          // Scroll to element with offset for fixed navigation
          const navHeight = 80; // Approximate height of navigation
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - navHeight;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero sekce */}
      <section className="relative min-h-screen flex items-center">
        {/* Background image */}
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/2e0b1e22c1b74afa84b035f494e9bd64/0be94a45643f5b2f1e55b45db646b3a4.png"
            alt="AI Technology Background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        {/* Content */}
        <div className="relative w-full py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-5xl mx-auto">
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
                Detailní přehled našich AI služeb
              </h1>
              <p className="text-2xl md:text-3xl text-gray-200 mb-12 leading-relaxed">
                10 hlavních kategorií s detailními specifikacemi jednotlivých služeb pro komplexní AI transformaci vaší firmy
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link to="/kontakt">
                  <Button size="lg" className="border-2 border-white text-white bg-transparent hover:bg-white hover:text-black text-lg px-8 py-4">
                    <i className="ri-file-text-line mr-2"></i>
                    Vyplnit formulář
                  </Button>
                </Link>
                <Link to="/sluzby">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-black text-lg px-8 py-4">
                    <i className="ri-arrow-left-line mr-2"></i>
                    Zpět na přehled
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Navigation - Moved to front */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Rychlá navigace</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {services.map((service, index) => (
              <button
                key={index}
                onClick={() => {
                  const element = document.getElementById(service.id);
                  if (element) {
                    const navHeight = 80;
                    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                    const offsetPosition = elementPosition - navHeight;
                    
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }}
                className="p-3 bg-white rounded-lg border border-gray-200 hover:border-black hover:shadow-sm transition-all text-center cursor-pointer"
              >
                <i className={`${service.icon} text-black text-lg mb-2 block`}></i>
                <span className="text-xs font-medium text-gray-700">{service.title.replace(/^\d+\.\s/, '')}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services with permanently expanded categories */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {services.map((service, serviceIndex) => (
              <div key={serviceIndex} id={service.id} className="border border-gray-200 rounded-2xl overflow-hidden scroll-mt-20">
                {/* Category Header - Always Visible */}
                <div className="p-6 bg-gray-50">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                      <i className={`${service.icon} text-black text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-gray-900 mb-1">{service.title}</h2>
                      <p className="text-gray-600">{service.shortDesc}</p>
                      <p className="text-sm text-gray-500 mt-1">{service.subcategories.length} služeb</p>
                    </div>
                  </div>
                </div>

                {/* Subcategories - Always Expanded */}
                <div className="p-6 bg-white">
                  <div className="space-y-4">
                    {service.subcategories.map((subcategory, subcategoryIndex) => (
                      <div key={subcategoryIndex} id={subcategory.id} className="border-l-4 border-gray-200 pl-6 scroll-mt-20">
                        <div className="flex items-start space-x-3">
                          <i className="ri-arrow-right-s-line text-black text-lg mt-0.5"></i>
                          <h3 className="text-lg font-semibold text-gray-900">{subcategory.name}</h3>
                        </div>
                        <p className="text-gray-700 leading-relaxed ml-6">{subcategory.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Máte otázky k našim službám?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Rádi vám poradíme s výběrem nejvhodnějšího řešení pro vaši firmu
          </p>
          <div className="flex justify-center">
            <Link to="/kontakt">
              <Button size="lg" className="border-2 border-white text-white bg-transparent hover:bg-white hover:text-black">
                <i className="ri-file-text-line mr-2"></i>
                Vyplnit formulář
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}